/**
 * Клиент аналитического протокола.
 *
 * Ход устроен так: клиент считает контекст на своей редакции данных, сервер
 * спрашивает модель, модель просит расчёты — и расчёты выполняются **здесь**,
 * тем же ядром, что и всё остальное. Так у продукта одна арифметика, а модель
 * не получает доступа к данным даже косвенно.
 *
 * Два правила, без которых путь ломается:
 *
 * - **устаревший ответ не применяется**: пока модель думала, человек мог
 *   изменить данные, и ответ про прежние числа теперь неверен;
 * - **ответ только от модели**: «Спроси SKOKA» — разговор с ИИ. Не справилась
 *   модель — человек видит «не получилось, повторить», а не шаблон,
 *   выдающий себя за ответ на его вопрос. Прежде на «а если после 16-го
 *   картой?» подставлялся пересказ «Сейчас главное».
 */
import {
  activeAllocation, add, addDays, advisorContext, clampPos, computeAvailable, diffDays, rhythmOf,
  incomeOutlook, liquidOf, proposalFrom, reservedOf, runTool, share, streamFingerprint, sub,
  MAX_TOOL_ROUNDS, ZERO,
  type AdvisorAnswer, type AdvisorContext, type HomeFacts, type Proposal, type ToolCall,
  type ToolError, type ToolResult, type ToolRunInput,
} from '@skoka/core';
import { api, unwrap } from './sync.js';
import { allocationViewOf, incomeFacts, stableSnapshot, type AppState } from './store.js';

/** Что вернул ход советника. */
export type AdvisorOutcome =
  | {
    kind: 'answer'; answer: AdvisorAnswer; context: AdvisorContext;
    /**
     * Что можно применить — из посчитанного, а не из слов модели.
     *
     * Отсутствует, когда применять нечего: календарь платежей меняет знание
     * человека, а не его план.
     */
    proposal?: Proposal;
    /** Версия данных, на которой всё это посчитано. */
    stateVersion: string;
  }
  /** Уточнение. `analysisId` возвращается, чтобы ответ продолжил тот же разбор. */
  | {
    kind: 'question'; question: string; context: AdvisorContext; analysisId: string;
    /** Готовые ответы — кнопками под вопросом. */
    options?: string[];
  }
  /** Данные изменились, пока шёл ответ: показывать его нельзя. */
  | { kind: 'stale' }
  /**
   * Модель не ответила или ответила не по контракту.
   *
   * Отдельное состояние, а не запасной ответ: шаблон вместо модели человек
   * читает как ответ на свой вопрос — и получает не то, что спрашивал.
   */
  | {
    kind: 'failed';
    /**
     * Сервер отказал по доступу: кончились вопросы, нужен Pro или аккаунт.
     * Повторять такой вопрос бессмысленно — окно показывает, что делать.
     */
    refused?: { reason: 'quota' | 'tier' | 'signup'; message: string };
  };

/** Отказ сервера по доступу — не сбой модели. */
class AdvisorRefused extends Error {
  constructor(readonly reason: 'quota' | 'tier' | 'signup', message: string) { super(message); }
}

/** Уточнение и ответ на него — память разбора между запросами. */
export interface ClarifyTurn { question: string; answer: string }

/**
 * Подпись состояния.
 *
 * Нужна, чтобы отличить ответ про сегодняшние данные от ответа про те, что
 * были минуту назад. Полный снимок для этого гонять незачем — достаточно
 * того, что меняется при любой правке.
 */
export function stateVersion(s: AppState): string {
  // Считается по значениям, а не по количеству записей.
  //
  // Счётчики не менялись от правки: платёж, исправленный с 8 000 на 90 000 ₽,
  // давал ту же подпись — и ответ, посчитанный до правки, признавался
  // актуальным. Человек видел совет про деньги, которых у него уже нет.
  const parts = [
    s.today,
    ...s.accounts.map((a) => `a${a.id}:${a.balance}:${a.isLiquid ? 1 : 0}:${a.balanceAsOf ?? ''}`),
    // Отпечаток источника включает ритм: смена недельного на двухнедельный
    // меняет деньги, а подпись «10 числа» у обоих была одна.
    ...s.incomeStreams.map((i) => `i${streamFingerprint(i)}`),
    `obligationsKnown:${s.obligationsKnown === true}`,
    ...s.obligations.map((o) => [
      `o${o.id}`, o.amount, o.amountEstimate ?? '', o.dayOfMonth,
      o.schedule?.anchor ?? '', o.schedule?.n ?? '', o.schedule?.unit ?? '',
      o.isActive ? 1 : 0,
      // Факты оплаты — часть картины: подтверждённый платёж меняет ответ.
      (o.payments ?? []).map((p) => `${p.scheduledDate}=${p.paid}`).join(','),
    ].join(':')),
    ...s.goals.map((g) => `g${g.id}:${g.targetAmount}:${g.currentAmount}:${g.monthlyContribution}:${g.status}`),
    ...s.loans.map((l) => `l${l.id}:${l.principal}:${l.payment}:${l.ratePct}`),
    // Дни выписки и платежа тоже: по ним прогноз ставит платёж за покупки
    // после выписки, и смена дня меняет ответ.
    ...s.creditTerms.map((c) => [
      `c${c.accountId}`, c.statementDebt, c.reserved, c.limit,
      c.statementDay ?? '', c.paymentDay, c.graceDays,
    ].join(':')),
    ...s.reserves.map((r) => `r${r.id}:${r.amount}:${r.forId ?? ''}`),
    // Дата плановой траты — `expectedDate`. Прежнее `dueDate` не существует
    // вовсе: подпись молча получала `undefined`, и перенос покупки с сентября
    // на октябрь не делал прежний совет устаревшим.
    ...s.plannedExpenses.map((p) => `p${p.id}:${p.amount}:${p.expectedDate}:${p.status}`),
    `n${JSON.stringify(s.baselines)}`,
    `t${s.transactions.length}:${s.transactions[s.transactions.length - 1]?.id ?? ''}`,
    s.focusOverride ?? '',
    s.planMode ?? '',
    // Режим и остаток буфера входят в отпечаток: разбор, сделанный до
    // выплаты себе, после неё устарел.
    `sb${(s.stablePlans ?? []).map((x) => `${x.id}:${x.status}:${x.payout}`).join(',')}`,
    `sp${(s.stablePayouts ?? []).length}`,
    JSON.stringify(s.tradeoffChoice ?? null),
  ];
  return parts.join('|');
}

/**
 * Числа главной — те же, что человек видит на экране.
 *
 * Считаются здесь теми же функциями, что и главная. «Можно тратить» —
 * расчёт ядра, без принятой раскладки: так же, как крупное число.
 */
export function homeFacts(s: AppState): HomeFacts | undefined {
  if (s.isEmpty) return undefined;
  const result = computeAvailable({
    ...s,
    expectations: s.expectedIncomes,
    incomeFacts: incomeFacts(s.transactions),
  });
  const onAccounts = liquidOf(s.accounts);
  const setAside = reservedOf(s.accounts, s.reserves);
  // То же число, что на главной: без раскладки, по расчёту ядра.
  const spend = clampPos(result.available);
  const spendUntil = result.horizon.basis === 'confirmed' ? result.horizonDate : null;
  const days = spendUntil ? Math.max(1, diffDays(s.today, spendUntil)) : 0;
  // Тем же вызовом, что и главная: отрезок считается по общему прогнозу.
  const outlook = stableSnapshot(s) ? null : incomeOutlook({
    today: s.today, horizon: spendUntil,
    accounts: s.accounts, creditTerms: s.creditTerms,
    incomeStreams: s.incomeStreams, obligations: s.obligations,
    reserves: s.reserves,
    needBaseline: s.baselines.need, wantBaseline: s.baselines.want,
    ...(s.trackingStartedAt ? { trackingStartedAt: s.trackingStartedAt } : {}),
    incomeFacts: incomeFacts(s.transactions),
  });
  return {
    spendUntil,
    spend,
    perDay: days > 0 ? share(spend, 1 / days) : ZERO,
    onAccounts,
    setAside,
    notSetAside: sub(onAccounts, setAside),
    ...(outlook ? {
      afterIncome: {
        from: outlook.from, to: outlook.to, income: outlook.income,
        payments: outlook.payments, living: outlook.living, left: outlook.left,
      },
    } : {}),
  };
}

/** Сколько операций отдаём модели: за 90 дней, не больше 300 последних. */
const DATA_DAYS = 90;
const DATA_OPERATIONS = 300;

/**
 * Полная картина денег для модели — в рублях и словами.
 *
 * Советник должен видеть всё, что видит человек: не только посчитанные
 * итоги, но и сами счета, условия карт, платежи с оплатами и операции. На
 * тех же данных владельца модель с рассуждением верно ответила на шесть
 * вопросов из семи, в том числе «на что уходят деньги» и «успею ли закрыть
 * карту без процентов», — ответить на них по одним итогам нельзя
 * (30.09.2026, решение владельца).
 *
 * Внутренние идентификаторы не отдаём — только названия: модель пишет их
 * в ответе человеку.
 */
export function advisorData(s: AppState): Record<string, unknown> {
  const rub = (m: number | undefined | null) => (typeof m === 'number' ? Math.round(m) / 100 : null);
  const acc = (id?: string | null) => (id ? s.accounts.find((a) => a.id === id)?.name ?? null : null);
  const cat = (id?: string | null) => (id ? s.categories.find((c) => c.id === id)?.name ?? null : null);
  const KIND: Record<string, string> = { debit: 'дебетовый', savings: 'накопительный', credit_card: 'кредитная карта', cash: 'наличные' };
  const UNIT: Record<string, string> = { day: 'день', week: 'неделю', month: 'месяц', year: 'год' };
  const since = addDays(s.today, -DATA_DAYS);
  const ops = s.transactions
    .filter((t) => t.occurredDate >= since)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .slice(-DATA_OPERATIONS);
  return {
    сегодня: s.today,
    счета: s.accounts.filter((a) => !a.archived).map((a) => ({
      название: a.name, тип: KIND[a.kind] ?? a.kind, остаток: rub(a.balance),
      деньги_для_трат: a.isLiquid && a.kind !== 'credit_card',
    })),
    кредитные_карты: s.creditTerms.map((t) => {
      const a = s.accounts.find((x) => x.id === t.accountId);
      return {
        карта: a?.name ?? null, текущий_долг: a ? rub(-a.balance) : null, лимит: rub(t.limit),
        день_выписки: t.statementDay ?? null, день_платежа: t.paymentDay, льготный_период_дней: t.graceDays ?? null,
        сумма_к_оплате_по_последней_выписке: rub(t.statementDebt), уже_внесено_после_выписки: rub(t.reserved),
        минимальный_платёж: { процент: t.minPaymentPct ?? null, не_меньше: rub(t.minPaymentFloor ?? null) },
      };
    }),
    кредиты: (s.loans ?? []).map((l) => ({
      название: l.name, остаток_долга: rub(l.principal), ставка_процентов: l.ratePct, платёж: rub(l.payment), число_платежа: l.paymentDay,
    })),
    доходы: s.incomeStreams.filter((i) => i.isActive !== false).map((i) => {
      // Ритм — через `rhythmOf`: он сам читает старую запись, а считать по
      // устаревшему числу месяца нельзя (недельный доход стал бы месячным).
      const r = rhythmOf(i);
      return {
        название: i.name, сумма: rub(i.amount), сумма_переменная: !!i.isVariable,
        когда: r.kind === 'dates' ? `числа ${r.days.join(', ')}`
          : r.kind === 'weekly' ? `раз в неделю от ${r.anchor}`
            : r.kind === 'biweekly' ? `раз в две недели от ${r.anchor}`
              : r.kind === 'irregular' ? 'когда как' : 'больше не приходит',
        на_счёт: acc(i.accountId),
      };
    }),
    обязательные_платежи: s.obligations.filter((o) => o.isActive).map((o) => ({
      название: o.name, сумма: rub(o.amount), число_месяца: o.dayOfMonth,
      расписание: !o.schedule ? 'каждый месяц'
        : o.schedule.kind === 'once' ? `разово ${o.schedule.anchor}`
          : `раз в ${o.schedule.n && o.schedule.n > 1 ? `${o.schedule.n} ` : ''}${UNIT[o.schedule.unit ?? 'month']}`,
      со_счёта: acc(o.accountId),
      оплаты: (o.payments ?? []).slice(-3).map((p) => ({ за: p.scheduledDate, оплачено: rub(p.paid) })),
    })),
    отложено_в_конвертах: s.reserves.map((r) => ({
      на_счёте: acc(r.accountId),
      под: r.forKind === 'credit' ? `платёж по карте ${acc(r.forId) ?? ''}`
        : r.forKind === 'obligation' ? `платёж ${s.obligations.find((o) => o.id === r.forId)?.name ?? ''}`
          : r.forKind === 'goal' ? `цель ${s.goals.find((g) => g.id === r.forId)?.name ?? ''}` : r.forKind,
      сумма: rub(r.amount),
    })),
    цели: s.goals.map((g) => ({
      название: g.name, нужно: rub(g.targetAmount), накоплено: rub(g.currentAmount), срок: g.targetDate ?? null,
      взнос_в_месяц: rub(g.monthlyContribution), статус: g.status,
    })),
    обычные_расходы_в_месяц: {
      повседневные_нужды: s.baselines.need.known ? rub(s.baselines.need.value) : null,
      свободные_траты: s.baselines.want.known ? rub(s.baselines.want.value) : null,
    },
    операции: ops.map((t) => ({
      дата: t.occurredDate,
      тип: t.type === 'expense' ? 'расход' : t.type === 'income' ? 'поступление' : t.type === 'transfer' ? 'перевод' : t.type,
      сумма: rub(t.amount),
      ...(t.categoryId ? { категория: cat(t.categoryId) } : {}),
      ...(t.type === 'expense' ? { часть_плана: t.part === 'want' ? 'свободные' : 'нужды' } : {}),
      счёт: acc(t.accountId),
      ...(t.counterAccountId ? { куда: acc(t.counterAccountId) } : {}),
      ...(t.note ? { заметка: t.note } : {}),
    })),
    операций_за_период: `последние ${DATA_DAYS} дней, показано ${ops.length}`,
  };
}

/** Контекст советника из состояния приложения. */
export function contextOf(s: AppState): AdvisorContext {
  const home = homeFacts(s);
  return advisorContext({
    ...(home ? { home } : {}),
    data: advisorData(s),
    today: s.today,
    stateVersion: stateVersion(s),
    accounts: s.accounts,
    creditTerms: s.creditTerms,
    incomeStreams: s.incomeStreams,
    obligations: s.obligations,
    obligationsKnown: s.obligationsKnown === true,
    goals: s.goals,
    plannedExpenses: s.plannedExpenses,
    reserves: s.reserves,
    loans: s.loans,
    needBaseline: s.baselines.need,
    wantBaseline: s.baselines.want,
    goalEvents: s.goalEvents,
    transactions: s.transactions,
    ...futureOf(s),
    // Без стабильного бюджета советник посоветовал бы «дотянуть до
    // зарплаты» тому, у кого зарплаты нет, а буфер счёл бы свободными
    // деньгами и предложил бы их потратить.
    ...(stableSnapshot(s) ? { stable: stableSnapshot(s)!.outlook } : {}),
  });
}

interface ModelResponse {
  type: 'clarify' | 'tools' | 'analysis' | 'insufficient';
  question?: string;
  options?: string[];
  toolCalls?: ToolCall[];
  headline?: string;
  tone?: AdvisorAnswer['tone'];
  conditions?: AdvisorAnswer['conditions'];
  conditionsNote?: string;
  figures?: AdvisorAnswer['figures'];
  warning?: string;
  details?: string[];
  basis?: string;
  action?: string;
  limits?: string[];
  alternatives?: { label: string; effect: string }[];
  missing?: string[];
}

/** Ход советника на готовом контексте: состояние приложения не требуется. */
export interface RunInput {
  context: AdvisorContext;
  /** Данные, на которых выполняются инструменты. Та же редакция, что у контекста. */
  tools: Omit<ToolRunInput, 'context'>;
  question?: string;
  analysisId?: string;
  history?: ClarifyTurn[];
  signal?: AbortSignal;
  currentVersion?: () => string;
}

export interface AskInput {
  state: AppState;
  question?: string;
  /**
   * Идентификатор разбора.
   *
   * Один на всю задачу: уточнения и расчёты внутри неё — это продолжение
   * начатого, а не новые разборы. Без него каждый ход считался бы отдельным,
   * и человек платил бы единицей за каждый вопрос модели к себе самому.
   */
  analysisId?: string;
  /** Что уже спросили и что человек ответил. */
  history?: ClarifyTurn[];
  /** Позволяет отменить ход, когда человек ушёл с экрана. */
  signal?: AbortSignal;
  /** Проверка актуальности перед показом: данные могли измениться. */
  currentVersion?: () => string;
}

/**
 * Задать вопрос советнику.
 *
 * Возвращает ответ, уточняющий вопрос либо признак устаревания. Ошибку не
 * бросает: отказ модели — это нормальное состояние продукта, а не сбой.
 */
export async function askAdvisor(input: AskInput): Promise<AdvisorOutcome> {
  const { state, ...rest } = input;
  return runAdvisor({ ...rest, context: contextOf(state), tools: toolInput(state) });
}

/**
 * Ход советника на готовом контексте.
 *
 * Отделён от состояния приложения затем, что первый разбор человек получает
 * **до сохранения** — по черновику настройки. Путь при этом обязан быть тем
 * же самым: одна проверка чисел, один список инструментов, один запасной
 * ответ. Второй путь «почти такой же» разошёлся бы с первым, и разошёлся бы
 * незаметно.
 */
export async function runAdvisor(input: RunInput): Promise<AdvisorOutcome> {
  const { context } = input;
  const version = context.stateVersion;
  const toolResults: ToolResult[] = [];
  /**
   * Ошибки расчётов уходят модели вместе с результатами. Без них она не
   * знала, что вызов не удался, и повторяла его же, пока ход не кончался
   * отказом: «Сколько уйдёт на платежи в октябре?» трижды звало погашение
   * кредита без идентификатора (30.09.2026).
   */
  const toolErrors: ToolError[] = [];
  /*
    Номер разбора — тот, под которым сервер его ведёт.

    Сервер заводит новый, если присланный уже оплачен ответом. Прежде клиент
    продолжал слать старый, и каждый раунд расчётов открывал на сервере ещё
    один разбор: вопрос с двумя расчётами занимал три единицы вместо одной.
  */
  let analysisId = input.analysisId ?? newAnalysisId();
  const history = input.history ?? [];
  let lastTool: ToolResult | undefined;
  // Не результат, а сам вызов: применяется то, что было посчитано, и ровно
  // теми числами, которые человек видел на экране.
  let lastCall: ToolCall | undefined;

  for (let round = 0; round <= MAX_TOOL_ROUNDS; round++) {
    let model: ModelResponse | null;
    try {
      const reply = await callModel({
        context,
        ...(input.question ? { question: input.question } : {}),
        toolResults,
        toolErrors,
        history,
        analysisId,
        round,
        ...(input.signal ? { signal: input.signal } : {}),
      });
      model = reply.response;
      if (reply.analysisId) analysisId = reply.analysisId;
    } catch (e) {
      if (e instanceof AdvisorRefused) {
        return { kind: 'failed', refused: { reason: e.reason, message: e.message } };
      }
      // Сеть, отказ провайдера, нарушенный контракт — человек видит
      // «не получилось, повторить». Единицу разбора сервер уже вернул.
      return { kind: 'failed' };
    }
    if (!model) return { kind: 'failed' };

    if (model.type === 'tools' && Array.isArray(model.toolCalls)) {
      // Расчёты выполняются на **той же** редакции данных, что и контекст.
      for (const call of model.toolCalls.slice(0, 3)) {
        const r = runTool({ ...input.tools, context }, call);
        if ('facts' in r) {
          toolResults.push(r);
          lastTool = r;
          lastCall = call;
        } else {
          toolErrors.push(r);
        }
      }
      continue;
    }

    if (model.type === 'clarify' && model.question) {
      if (stale(input, version)) return { kind: 'stale' };
      return {
        kind: 'question', question: model.question, context, analysisId,
        ...(model.options?.length ? { options: model.options } : {}),
      };
    }

    if (model.type === 'analysis' && model.headline) {
      return finish(input, context, {
        headline: model.headline,
        ...(model.tone ? { tone: model.tone } : {}),
        ...(model.conditions?.length ? { conditions: model.conditions } : {}),
        ...(model.conditionsNote ? { conditionsNote: model.conditionsNote } : {}),
        ...(model.figures?.length ? { figures: model.figures } : {}),
        ...(model.warning ? { warning: model.warning } : {}),
        ...(model.details?.length ? { details: model.details } : {}),
        basis: model.basis ?? '',
        action: model.action ?? '',
        // Ограничение расчёта, которое модель уже пересказала, второй раз не
        // пишем: «Потребуется кредитных денег 95 311,68 ₽» стояло дважды.
        limits: uniqueLimits([...(model.limits ?? []), ...(lastTool?.limits ?? [])]),
        alternatives: model.alternatives ?? lastTool?.alternatives ?? [],
        withoutModel: false,
      }, lastCall);
    }

    if (model.type === 'insufficient') {
      // Ответ модели и ничего сверх него: прежде сюда подмешивались шаг и
      // варианты из «Сейчас главное».
      return finish(input, context, {
        headline: 'Не хватает данных для ответа',
        insufficient: true,
        basis: model.question ?? '',
        action: model.question ? 'Ответь на вопрос выше — и SKOKA посчитает' : 'Дополни недостающие данные',
        limits: model.missing ?? [],
        alternatives: [],
        withoutModel: false,
      }, lastCall);
    }

    return { kind: 'failed' };
  }

  // Раунды исчерпаны — ответа модели нет.
  return { kind: 'failed' };
}

function finish(
  input: RunInput, context: AdvisorContext, answer: AdvisorAnswer, call?: ToolCall,
): AdvisorOutcome {
  if (stale(input, context.stateVersion)) return { kind: 'stale' };
  const proposal = call ? proposalFrom(call) : null;
  return {
    kind: 'answer', answer, context, stateVersion: context.stateVersion,
    ...(proposal ? { proposal } : {}),
  };
}

/** Данные изменились, пока шёл ответ. */
function stale(input: RunInput, version: string): boolean {
  return input.currentVersion !== undefined && input.currentVersion() !== version;
}

/** Вход инструментов: то же состояние, что и у контекста. */
export function toolInput(s: AppState): Omit<ToolRunInput, 'context'> {
  return {
    today: s.today,
    stateVersion: stateVersion(s),
    accounts: s.accounts,
    creditTerms: s.creditTerms,
    incomeStreams: s.incomeStreams,
    obligations: s.obligations,
    goals: s.goals,
    plannedExpenses: s.plannedExpenses,
    reserves: s.reserves,
    loans: s.loans,
    needBaseline: s.baselines.need,
    wantBaseline: s.baselines.want,
    goalEvents: s.goalEvents,
    transactions: s.transactions,
    ...futureOf(s),
  };
}

/**
 * То, по чему главная считает будущее: начало учёта, случившиеся приходы и
 * ожидания.
 *
 * Без них советник и проверка покупки считали бы горизонт и просрочку не
 * так, как главная: неотмеченный платёж был бы занятыми деньгами на экране
 * и свободными в ответе.
 */
function futureOf(s: AppState): Pick<ToolRunInput, 'trackingStartedAt' | 'incomeFacts' | 'expectations'> {
  return {
    ...(s.trackingStartedAt ? { trackingStartedAt: s.trackingStartedAt } : {}),
    incomeFacts: incomeFacts(s.transactions),
    expectations: s.expectedIncomes,
  };
}

/**
 * Новый идентификатор разбора.
 *
 * `randomUUID` есть не везде (нужен защищённый контекст), поэтому запасной
 * вариант обязателен: разбор не должен падать из-за способа получить ключ.
 */
function newAnalysisId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function callModel(body: {
  context: AdvisorContext;
  question?: string;
  toolResults: ToolResult[];
  toolErrors: ToolError[];
  history: ClarifyTurn[];
  analysisId: string;
  round: number;
  signal?: AbortSignal;
}): Promise<{ response: ModelResponse | null; analysisId?: string }> {
  const { signal, ...payload } = body;
  const res = await api('/ai/analyze', {
    method: 'POST',
    body: JSON.stringify(payload),
    ...(signal ? { signal } : {}),
  });
  // Отказ по доступу различаем по причине, а не по коду: 429 бывает и
  // «слишком часто» — тогда стоит просто повторить.
  if (res.status === 402 || res.status === 429) {
    const body = await res.clone().json().catch(() => ({})) as { error?: string; reason?: string };
    if (body.reason === 'quota' || body.reason === 'tier' || body.reason === 'signup') {
      throw new AdvisorRefused(body.reason, body.error ?? '');
    }
  }
  const parsed = await unwrap(res) as { response?: ModelResponse; analysisId?: string };
  return {
    response: parsed.response ?? null,
    ...(typeof parsed.analysisId === 'string' ? { analysisId: parsed.analysisId } : {}),
  };
}

/** Ограничения без повторов: сравниваются без точки в конце и регистра. */
function uniqueLimits(limits: string[]): string[] {
  const seen = new Set<string>();
  return limits.filter((l) => {
    const key = l.trim().replace(/\.$/, '').toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
